import React from 'react';
import { Table, Empty, Select, Button, Space, Popconfirm, Input, Dropdown, Modal, Descriptions, Tag, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, CalendarOutlined, EyeOutlined, DownOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { holidaysApi } from '../../../utils/api';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import moment from 'moment-jalaali';
import { BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../../constants/ui';

const { Option } = Select;

const HolidaysListView = ({
  data = [],
  loading = false,
  onDelete,
  onEdit,
  onFilterChange,
  onBulkAction,
  deptOptions = [],
  designationOptions = [],
  employmentLabels = {},
  toJalaliDate = (v) => v,
  getWeekday = () => '',
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [searchText, setSearchText] = React.useState('');
  const [jYear, setJYear] = React.useState(() => moment().format('jYYYY'));
  const [jMonth, setJMonth] = React.useState(() => moment().format('jM').padStart(2, '0'));
  const [bulkActionLoading, setBulkActionLoading] = React.useState(false);
  const [viewModalVisible, setViewModalVisible] = React.useState(false);
  const [viewLoading, setViewLoading] = React.useState(false);
  const [viewData, setViewData] = React.useState(null);
  const isFirstRender = React.useRef(true);

  const deptMap = React.useMemo(() => {
    const map = {};
    deptOptions.forEach((d) => {
      map[String(d.id)] = d.team_name || d.name;
    });
    return map;
  }, [deptOptions]);

  const desigMap = React.useMemo(() => {
    const map = {};
    designationOptions.forEach((d) => {
      map[String(d.id)] = d.name;
    });
    return map;
  }, [designationOptions]);

  const renderDepartments = (ids = []) => {
    if (!ids.length) return '-';
    return ids.map((id) => deptMap[String(id)] || id).join(' / ');
  };

  const renderDesignations = (ids = []) => {
    if (!ids.length) return '-';
    return ids.map((id) => desigMap[String(id)] || id).join(' / ');
  };

  const renderEmployment = (arr = []) => {
    if (!arr.length) return '-';
    return arr.map((k) => employmentLabels[k] || k).join(' / ');
  };

  const handleView = async (id) => {
    setViewModalVisible(true);
    setViewLoading(true);
    setViewData(null);
    try {
      const res = await holidaysApi.getById(id);
      const holiday = res?.data || res || {};
      setViewData(holiday);
    } catch (error) {
      console.error('Error fetching holiday details:', error);
      message.error('خطا در دریافت اطلاعات تعطیلی');
      setViewModalVisible(false);
    } finally {
      setViewLoading(false);
    }
  };

  // عملیات گروهی
  const handleBulkAction = async (actionType) => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک تعطیلی را انتخاب کنید');
      return;
    }

    Modal.confirm({
      title: 'تأیید عملیات گروهی',
      icon: <ExclamationCircleOutlined />,
      content: `آیا از حذف ${selectedRowKeys.length} تعطیلی انتخاب شده اطمینان دارید؟`,
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          if (onBulkAction) {
            await onBulkAction(actionType, selectedRowKeys);
            setSelectedRowKeys([]);
            message.success('عملیات گروهی با موفقیت انجام شد');
          }
        } catch (error) {
          console.error('خطا در عملیات گروهی:', error);
          message.error('خطا در انجام عملیات گروهی');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  // منوی عملیات گروهی
  const bulkActionMenuItems = [
    {
      key: 'delete',
      label: 'حذف تعطیلات',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleBulkAction('delete'),
    },
  ];

  // اعمال فیلترها با debounce
  React.useEffect(() => {
    // در اولین render اجرا نشود (چون Holidays.js خودش initial load را انجام می‌دهد)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!onFilterChange) return;

    const timer = setTimeout(() => {
      const filters = {};
      if (searchText) filters.occassion = searchText;
      
      // ارسال فیلتر بر اساس سال/ماه جلالی بدون تبدیل به میلادی
      if (jYear && jMonth) {
        filters.year = jYear;
        filters.month = jMonth;
      }
      
      onFilterChange(filters);
    }, 800); // debounce با تاخیر 800ms
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, jYear, jMonth]);

  const columns = [
    {
      title: 'تاریخ',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => String(a.date).localeCompare(String(b.date)),
      render: (text) => <span style={{ fontFamily: 'CustomFont' }}>{toJalaliDate(text)}</span>,
    },
    {
      title: 'مناسبت',
      dataIndex: 'occasion',
      key: 'occasion',
      sorter: (a, b) => a.occasion.localeCompare(b.occasion),
      render: (text) => <span style={{ fontFamily: 'CustomFont' }}>{text}</span>,
    },
    {
      title: 'روز',
      dataIndex: 'dayOfWeek',
      key: 'dayOfWeek',
      sorter: (a, b) => String(a.dayOfWeek || '').localeCompare(String(b.dayOfWeek || '')),
      render: (_, record) => <span style={{ fontFamily: 'CustomFont' }}>{getWeekday(record.date)}</span>,
    },
    {
      title: 'دپارتمان',
      dataIndex: 'department',
      key: 'department',
      sorter: (a, b) => (a.department || '').localeCompare(b.department || ''),
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {renderDepartments(record.department)}
        </span>
      ),
    },
    {
      title: 'عنوان شغلی',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      sorter: (a, b) => (a.jobTitle || '').localeCompare(b.jobTitle || ''),
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {renderDesignations(record.jobTitle)}
        </span>
      ),
    },
    {
      title: 'نوع استخدام',
      dataIndex: 'employmentType',
      key: 'employmentType',
      sorter: (a, b) => (a.employmentType || '').localeCompare(b.employmentType || ''),
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {renderEmployment(record.employmentType)}
        </span>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.id)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            style={{ fontFamily: 'CustomFont' }}
            title="مشاهده"
          />
          {onEdit ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record.id)}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              style={{ fontFamily: 'CustomFont' }}
              title="ویرایش"
            />
          ) : null}
          {onDelete ? (
            <Popconfirm
              title="حذف تعطیلی"
              description="آیا از حذف این تعطیلی اطمینان دارید؟"
              onConfirm={async () => {
                await onDelete(record.id);
                message.success('حذف شد');
              }}
              okText="بله، حذف کن"
              cancelText="لغو"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ fontFamily: 'CustomFont' }}
                title="حذف"
              />
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const parseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const mappedData = React.useMemo(() => {
    return (data || []).map((item) => ({
      ...item,
      occasion: item.occassion || item.occasion,
      department: parseArray(item.department_id_json) || parseArray(item.department),
      jobTitle: parseArray(item.designation_id_json) || parseArray(item.jobTitle),
      employmentType: parseArray(item.employment_type_json) || parseArray(item.employmentType),
    }));
  }, [data]);

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {/* فیلترها - ریسپانسیو */}
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input.Search
          placeholder="جستجوی مناسبت..."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ fontFamily: 'CustomFont' }}
          className="w-full sm:col-span-2 lg:col-span-1"
        />
        <div className="flex items-center gap-2">
          <CalendarOutlined className="text-gray-600 dark:text-gray-400 flex-shrink-0" />
          <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap text-sm">سال:</span>
          <Select
            value={jYear}
            onChange={setJYear}
            style={{ fontFamily: 'CustomFont', width: '100%' }}
          >
            <Option value="1404">۱۴۰۴</Option>
            <Option value="1403">۱۴۰۳</Option>
            <Option value="1402">۱۴۰۲</Option>
          </Select>
        </div>
        <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-1">
          <CalendarOutlined className="text-gray-600 dark:text-gray-400 flex-shrink-0" />
          <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap text-sm">ماه:</span>
          <Select
            value={jMonth}
            onChange={setJMonth}
            style={{ fontFamily: 'CustomFont', width: '100%' }}
          >
            <Option value="01">فروردین</Option>
            <Option value="02">اردیبهشت</Option>
            <Option value="03">خرداد</Option>
            <Option value="04">تیر</Option>
            <Option value="05">مرداد</Option>
            <Option value="06">شهریور</Option>
            <Option value="07">مهر</Option>
            <Option value="08">آبان</Option>
            <Option value="09">آذر</Option>
            <Option value="10">دی</Option>
            <Option value="11">بهمن</Option>
            <Option value="12">اسفند</Option>
          </Select>
        </div>
      </div>

      {/* عملیات گروهی */}
      {selectedRowKeys.length > 0 && (
        <div className={BULK_ACTION_BAR_CLASS}>
          <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
            {selectedRowKeys.length} مورد انتخاب شده
          </span>
          <Dropdown
            menu={{ items: bulkActionMenuItems }}
            trigger={['click']}
          >
            <Button
              type="primary"
              loading={bulkActionLoading}
              size="middle"
              className="border-0"
              icon={<DownOutlined />}
              style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
            >
              عملیات گروهی
            </Button>
          </Dropdown>
        </div>
      )}

      {/* جدول */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {loading ? (
          <div className="p-6">
            <TableSkeleton rows={5} columns={7} />
          </div>
        ) : (
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={mappedData}
            rowKey="id"
            loading={false}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => 
                `نمایش ${range[0]} تا ${range[1]} از ${total} ردیف`,
              locale: {
                items_per_page: 'ردیف در صفحه',
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  description={<span style={{ fontFamily: 'CustomFont' }}>هیچ داده ای در جدول وجود ندارد</span>}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            style={{ fontFamily: 'CustomFont' }}
          />
        )}
      </div>

      <Modal
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setViewData(null);
        }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <InfoCircleOutlined className="text-blue-500" />
            <span>جزئیات تعطیلی</span>
          </div>
        }
        footer={
          <Button
            onClick={() => {
              setViewModalVisible(false);
              setViewData(null);
            }}
            style={{ fontFamily: 'CustomFont', borderColor: '#d9d9d9', color: '#595959' }}
          >
            بستن
          </Button>
        }
        centered
        width={560}
        destroyOnClose
      >
        {viewLoading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : viewData ? (
          <Descriptions
            bordered
            column={1}
            size="middle"
            labelStyle={{ fontFamily: 'CustomFont', fontWeight: 'bold', width: 140, backgroundColor: '#fafafa' }}
            contentStyle={{ fontFamily: 'CustomFont' }}
          >
            <Descriptions.Item label="مناسبت">
              <Tag color="blue" style={{ fontSize: 14, padding: '2px 12px' }}>
                {viewData.occassion || viewData.occasion || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="تاریخ">
              {viewData.date ? (
                <span>
                  {toJalaliDate(viewData.date)}
                  <span className="text-gray-400 mr-2">({getWeekday(viewData.date)})</span>
                </span>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="دپارتمان">
              {(() => {
                const ids = parseArray(viewData.department_id_json);
                if (!ids.length) return <span className="text-gray-400">همه دپارتمان‌ها</span>;
                return (
                  <div className="flex flex-wrap gap-1">
                    {ids.map((id) => (
                      <Tag key={id} color="cyan">{deptMap[String(id)] || id}</Tag>
                    ))}
                  </div>
                );
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="عنوان شغلی">
              {(() => {
                const ids = parseArray(viewData.designation_id_json);
                if (!ids.length) return <span className="text-gray-400">همه عناوین شغلی</span>;
                return (
                  <div className="flex flex-wrap gap-1">
                    {ids.map((id) => (
                      <Tag key={id} color="purple">{desigMap[String(id)] || id}</Tag>
                    ))}
                  </div>
                );
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="نوع استخدام">
              {(() => {
                const types = parseArray(viewData.employment_type_json);
                if (!types.length) return <span className="text-gray-400">همه انواع استخدام</span>;
                return (
                  <div className="flex flex-wrap gap-1">
                    {types.map((k) => (
                      <Tag key={k} color="orange">{employmentLabels[k] || k}</Tag>
                    ))}
                  </div>
                );
              })()}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </div>
  );
};

export default HolidaysListView;
